// Panel "Autopilot" para el Overview del dossier:
//   - Stats arriba
//   - Botón "Lanzar autopilot" + plan que muestra qué va a hacer
//   - Log en vivo (polling cada 3s) de los searches del run activo
//   - Histórico de runs anteriores
//   - A la derecha: chat con tools (split en pantallas grandes,
//     stack en mobile)

import React, { useEffect, useRef, useState } from 'react';
import friendlyError from '../../utils/friendlyError';
import styled, { keyframes } from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  FaRobot,
  FaUser,
  FaPlay,
  FaPause,
  FaStop,
  FaInfinity,
  FaCheckCircle,
  FaExclamationCircle,
  FaPaperPlane,
  FaTrashAlt,
  FaCog,
  FaListUl,
  FaHistory,
} from 'react-icons/fa';
import {
  borderSoft,
  panelDeep,
  mono,
  accent,
  ErrorBox,
} from '../../styles/techComponents';
import {
  getAutoRunStatus,
  launchAutoRun,
  getDossierChatHistory,
  sendDossierChat,
  clearDossierChatHistory,
  setLoopState,
} from '../../services/investigationService';
import { CitePopup, wrapWithCitations } from '../UI/Citations';

const POLL_INTERVAL = 8000;

const Split = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 380px;
  gap: 1rem;
  @media (max-width: 1100px) { grid-template-columns: 1fr; }
`;

const ColLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  min-width: 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 8px;

  .cell {
    background: ${panelDeep};
    border: 1px solid ${borderSoft};
    border-radius: 8px;
    padding: 10px 12px;
    .label {
      font-family: ${mono};
      font-size: 0.6rem;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      color: var(--text-disabled);
    }
    .value { font-size: 1.55rem; color: ${accent}; font-weight: 700; }
  }
`;

const Card = styled.section`
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 10px;
  padding: 1rem 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.7rem;

  .head {
    display: flex; align-items: center; gap: 0.6rem;
    .title {
      flex: 1; color: var(--text-primary); font-weight: 600; font-size: 0.95rem;
      display: flex; align-items: center; gap: 0.5rem;
    }
    .sub {
      font-family: ${mono};
      font-size: 0.6rem; letter-spacing: 0.16em;
      text-transform: uppercase; color: var(--text-disabled);
    }
    .pill {
      padding: 3px 8px;
      border-radius: 999px;
      font-family: ${mono};
      font-size: 0.6rem;
      letter-spacing: 0.14em;
      text-transform: uppercase;
    }
    .pill-running { background: rgba(0, 180, 216, 0.13); color: ${accent}; border: 1px solid rgba(0, 180, 216, 0.35); }
    .pill-done { background: rgba(6, 214, 160, 0.08); color: var(--alert-success); border: 1px solid rgba(6, 214, 160, 0.3); }
    .pill-failed { background: rgba(239, 71, 111, 0.08); color: var(--alert-error); border: 1px solid rgba(239, 71, 111, 0.3); }
  }
`;

const PrimaryBtn = styled.button`
  background: rgba(0, 180, 216, 0.18);
  color: ${accent};
  border: 1px solid rgba(0, 180, 216, 0.4);
  padding: 9px 14px;
  border-radius: 8px;
  font-size: 0.86rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.18s ease;
  &:hover:not(:disabled) { background: rgba(0, 180, 216, 0.3); transform: translateY(-1px); }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const LogRow = styled.div`
  display: flex;
  gap: 0.55rem;
  align-items: flex-start;
  padding: 0.55rem 0.75rem;
  background: rgba(11, 19, 32, 0.45);
  border: 1px solid ${borderSoft};
  border-left: 3px solid ${({ $status }) =>
    $status === 'running' ? accent :
    $status === 'done' ? 'var(--alert-success)' :
    $status === 'failed' ? 'var(--alert-error)' :
    'rgba(255, 255, 255, 0.15)'};
  border-radius: 6px;

  .marker {
    flex-shrink: 0;
    margin-top: 1px;
    color: ${({ $status }) =>
      $status === 'running' ? accent :
      $status === 'done' ? 'var(--alert-success)' :
      $status === 'failed' ? 'var(--alert-error)' :
      'var(--text-disabled)'};
  }
  .body { flex: 1; min-width: 0; }
  .agent {
    font-family: ${mono};
    font-size: 0.65rem;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: ${accent};
  }
  .input {
    font-size: 0.78rem;
    color: var(--text-secondary);
    margin-top: 2px;
  }
  .meta {
    font-family: ${mono};
    font-size: 0.6rem;
    color: var(--text-disabled);
    margin-top: 2px;
  }
  .pulse {
    display: inline-block; width: 8px; height: 8px; border-radius: 50%;
    background: ${accent};
    animation: pulseAnim 1.5s ease-in-out infinite;
    margin-right: 4px;
  }
  @keyframes pulseAnim {
    0%, 100% { opacity: 0.3; transform: scale(1); }
    50% { opacity: 1; transform: scale(1.3); }
  }
`;

const PlanList = styled.ol`
  margin: 0; padding-left: 1.3rem;
  color: var(--text-secondary); font-size: 0.86rem;
  li { margin-bottom: 4px; }
`;

const dotBounce = keyframes`
  0%, 60%, 100% { transform: translateY(0); opacity: 0.35; }
  30%           { transform: translateY(-4px); opacity: 1; }
`;

const ChatWrap = styled.div`
  background: ${panelDeep};
  border: 1px solid rgba(0, 180, 216, 0.18);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-height: 720px;
  min-height: 480px;
  position: sticky;
  top: 1rem;
  @media (max-width: 1100px) {
    position: static;
    min-height: 460px;
  }
`;

const ChatHeader = styled.div`
  padding: 0.85rem 1rem;
  border-bottom: 1px solid ${borderSoft};
  background: linear-gradient(180deg, rgba(0, 180, 216, 0.07), transparent);
  display: flex; align-items: center; gap: 0.55rem;

  .icon {
    width: 28px; height: 28px; border-radius: 50%;
    background: rgba(0, 180, 216, 0.15);
    border: 1px solid rgba(0, 180, 216, 0.35);
    color: ${accent};
    display: flex; align-items: center; justify-content: center;
  }
  .title { flex: 1; color: var(--text-primary); font-weight: 600; font-size: 0.94rem; }
  .clear {
    background: transparent; border: 1px solid ${borderSoft};
    color: var(--text-disabled);
    width: 26px; height: 26px; border-radius: 5px; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    font-size: 0.7rem;
    &:hover { color: var(--alert-error); }
  }
`;

const ChatBody = styled.div`
  flex: 1; overflow-y: auto;
  padding: 0.85rem 0.95rem;
  display: flex; flex-direction: column; gap: 0.7rem;
  background: rgba(11, 19, 32, 0.25);
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 6px; }
`;

const Msg = styled.div`
  display: flex; gap: 0.55rem;
  .avatar {
    flex-shrink: 0; width: 26px; height: 26px;
    border-radius: 50%;
    background: ${({ $role }) => ($role === 'user' ? 'rgba(0, 180, 216, 0.1)' : 'rgba(6, 214, 160, 0.1)')};
    border: 1px solid ${borderSoft};
    color: ${({ $role }) => ($role === 'user' ? accent : 'var(--alert-success)')};
    display: flex; align-items: center; justify-content: center; font-size: 0.7rem;
  }
  .bubble {
    flex: 1; min-width: 0;
    background: rgba(11, 19, 32, 0.6);
    border: 1px solid ${borderSoft};
    border-radius: 7px;
    padding: 0.55rem 0.75rem;
    color: var(--text-secondary);
    font-size: 0.85rem;
    line-height: 1.5;
    p { margin: 0 0 0.4rem; } p:last-child { margin: 0; }
    table { border-collapse: collapse; font-size: 0.78rem; margin: 0.4rem 0; }
    th, td { border: 1px solid ${borderSoft}; padding: 3px 6px; }
    code { background: rgba(0,180,216,0.1); color: ${accent}; padding: 1px 4px; border-radius: 3px; font-size: 0.78rem; }
    a { color: ${accent}; }
  }
  .actions-pill {
    font-family: ${mono};
    font-size: 0.58rem;
    letter-spacing: 0.16em;
    color: var(--alert-success);
    margin-top: 4px;
  }
`;

const Typing = styled.div`
  display: inline-flex; gap: 4px; align-items: center;
  span {
    width: 5px; height: 5px; border-radius: 50%;
    background: ${accent};
    animation: ${dotBounce} 1.1s infinite;
    &:nth-child(2) { animation-delay: 0.15s; }
    &:nth-child(3) { animation-delay: 0.3s; }
  }
`;

const InputWrap = styled.form`
  border-top: 1px solid ${borderSoft};
  padding: 0.7rem 0.85rem;
  background: rgba(11, 19, 32, 0.4);
  display: flex; gap: 0.5rem;
`;

const ChatInput = styled.textarea`
  flex: 1; resize: none;
  min-height: 38px; max-height: 100px;
  background: rgba(11, 19, 32, 0.65);
  border: 1px solid ${borderSoft};
  border-radius: 6px;
  color: var(--text-primary);
  font-family: inherit; font-size: 0.84rem;
  padding: 0.5rem 0.7rem; outline: none;
  &:focus { border-color: rgba(0, 180, 216, 0.5); }
`;

const SendBtn = styled.button`
  flex-shrink: 0;
  width: 38px; height: 38px;
  border-radius: 6px;
  border: 1px solid rgba(0, 180, 216, 0.35);
  background: rgba(0, 180, 216, 0.15);
  color: ${accent};
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  &:hover:not(:disabled) { background: rgba(0, 180, 216, 0.3); }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const EntityChip = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 1px 7px;
  margin: 0 1px;
  font-family: ${mono};
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: var(--alert-warning, #f4a261);
  background: rgba(244, 162, 97, 0.1);
  border: 1px solid rgba(244, 162, 97, 0.4);
  border-radius: 4px;
  cursor: pointer;
  vertical-align: baseline;
  transition: all 0.15s ease;
  &:hover {
    background: rgba(244, 162, 97, 0.22);
    transform: translateY(-1px);
  }
`;

const AgentChip = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  margin: 0 2px;
  font-family: ${mono};
  font-size: 0.7rem;
  font-weight: 700;
  color: #62d59a;
  background: rgba(98, 213, 154, 0.1);
  border: 1px solid rgba(98, 213, 154, 0.4);
  border-radius: 4px;
  cursor: pointer;
  vertical-align: baseline;
  transition: all 0.15s ease;
  &:hover {
    background: rgba(98, 213, 154, 0.22);
    transform: translateY(-1px);
  }
`;

const EntityPopupBackdrop = styled.div`
  position: fixed; inset: 0; z-index: 1500;
  background: rgba(8, 14, 24, 0.7); backdrop-filter: blur(3px);
  display: flex; align-items: center; justify-content: center; padding: 1rem;
`;
const EntityPopupCard = styled.div`
  width: min(520px, 100%);
  max-height: calc(100vh - 2rem);
  display: flex;
  flex-direction: column;
  background: var(--color-primary);
  border: 1px solid rgba(244, 162, 97, 0.45);
  border-radius: 10px;
  padding: 1.1rem 1.25rem 1.25rem;
  color: var(--text-secondary);
  position: relative;
  overflow-y: auto;

  h3 { color: var(--text-primary); margin: 0 0 0.45rem; }
  .meta {
    font-family: ${mono};
    font-size: 0.62rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--text-disabled);
    margin-bottom: 0.7rem;
  }
  .summary {
    font-size: 0.88rem;
    line-height: 1.55;
    color: var(--text-secondary);
    background: rgba(11, 19, 32, 0.5);
    border: 1px solid ${borderSoft};
    border-left: 2px solid rgba(244, 162, 97, 0.5);
    border-radius: 6px;
    padding: 0.55rem 0.75rem;
    margin: 0 0 0.85rem;
    word-break: break-word;
  }
  .close {
    position: absolute; top: 12px; right: 14px;
    background: transparent; border: 1px solid ${borderSoft};
    color: var(--text-disabled);
    width: 26px; height: 26px; border-radius: 5px; cursor: pointer;
    &:hover { color: var(--alert-error); }
  }
  .missing {
    color: var(--alert-error);
    font-size: 0.85rem;
    background: rgba(239, 71, 111, 0.08);
    border: 1px solid rgba(239, 71, 111, 0.3);
    border-radius: 6px;
    padding: 0.55rem 0.7rem;
  }
`;

// Red de seguridad: si un mensaje viejo del asistente quedó guardado como
// el JSON crudo del LLM ({thought,actions,answer}), extraemos `answer`
// para mostrarlo como texto normal.
function displayAssistantContent(raw) {
  if (!raw) return '';
  const trimmed = String(raw).trim();
  if (!trimmed.startsWith('{') || !/"\s*answer\s*"\s*:/.test(trimmed)) {
    return raw;
  }
  // Intento 1: JSON.parse directo
  try {
    const obj = JSON.parse(trimmed);
    if (obj && typeof obj === 'object' && typeof obj.answer === 'string') {
      return obj.answer;
    }
  } catch {}
  // Intento 2: regex tolerante con string escapado
  const m = trimmed.match(/"answer"\s*:\s*"((?:[^"\\]|\\.)*)"/s);
  if (m) {
    try { return JSON.parse(`"${m[1]}"`); } catch { return m[1]; }
  }
  return raw;
}

const AGENT_LABELS = {
  entity_researcher: 'Investigar a fondo',
  connection_finder: 'Buscar conexiones',
  theory_evaluator: 'Evaluar hipótesis',
  timeline_builder: 'Armar cronología',
  report_synthesizer: 'Sintetizar informe',
};

// Detecta patrones accionables en texto: (id:N), id:N, agente entity_researcher, etc.
// y los reemplaza por chips clickeables.
function renderChatChips(node, { onEntity, onAgent }) {
  if (typeof node === 'string') {
    // Regex que matchea (id:N) o (ids 6, 7 y 8) o connection_finder, etc.
    // - Grupo 1: id paréntesis "(id:6)" o "(ids 21 y 22)"
    // - Grupo 2: agente
    const RE = /(\(\s*ids?\s*:?\s*[\d,\s y]+\))|\b(entity_researcher|connection_finder|theory_evaluator|timeline_builder|report_synthesizer)\b/g;
    const parts = [];
    let last = 0;
    let m;
    while ((m = RE.exec(node)) !== null) {
      if (m.index > last) parts.push(node.slice(last, m.index));
      if (m[1]) {
        const ids = (m[1].match(/\d+/g) || []).map(Number);
        parts.push({ __ent: true, ids, raw: m[1] });
      } else if (m[2]) {
        parts.push({ __agent: true, name: m[2] });
      }
      last = m.index + m[0].length;
    }
    if (last < node.length) parts.push(node.slice(last));
    if (parts.length === 1 && typeof parts[0] === 'string') return node;
    return parts.map((p, i) => {
      if (typeof p === 'string') return p;
      if (p.__ent) {
        return (
          <React.Fragment key={i}>
            (
            {p.ids.map((id, j) => (
              <React.Fragment key={id}>
                {j > 0 && p.ids.length > 2 && j < p.ids.length - 1 ? ', ' : j > 0 ? ' y ' : null}
                <EntityChip type="button" onClick={() => onEntity(id)} title={`Abrir entidad ${id}`}>
                  id:{id}
                </EntityChip>
              </React.Fragment>
            ))}
            )
          </React.Fragment>
        );
      }
      if (p.__agent) {
        const label = AGENT_LABELS[p.name] || p.name;
        return (
          <AgentChip key={i} type="button" onClick={() => onAgent(p.name)} title={`Ejecutar agente ${p.name}`}>
            ▶ {label}
          </AgentChip>
        );
      }
      return null;
    });
  }
  if (Array.isArray(node)) {
    return node.map((n, i) => (
      <React.Fragment key={i}>{renderChatChips(n, { onEntity, onAgent })}</React.Fragment>
    ));
  }
  if (React.isValidElement(node)) {
    const children = node.props.children;
    if (children === undefined || children === null) return node;
    return React.cloneElement(node, { ...node.props }, renderChatChips(children, { onEntity, onAgent }));
  }
  return node;
}

function wrapChatMarkdown({ onCite, onEntity, onAgent }) {
  // Componemos sobre wrapWithCitations: primero detecta citas, después chips.
  const baseWrap = wrapWithCitations(onCite);
  const wrap = (Tag) => ({ children, ...rest }) => {
    const cited = baseWrap[Tag] ? baseWrap[Tag]({ children, ...rest }).props.children : children;
    return <Tag {...rest}>{renderChatChips(cited, { onEntity, onAgent })}</Tag>;
  };
  return {
    p: wrap('p'),
    li: wrap('li'),
    td: wrap('td'),
    th: wrap('th'),
    strong: wrap('strong'),
    em: wrap('em'),
    h1: wrap('h1'),
    h2: wrap('h2'),
    h3: wrap('h3'),
    h4: wrap('h4'),
    blockquote: wrap('blockquote'),
  };
}

const SUGGESTIONS = [
  'Agregá a Manuel Passaglia como entidad y corré el research',
  'Lanzá el autopilot completo',
  'Resumime el estado del dossier',
  'Buscá si hay denuncias de corrupción contra Passaglia',
  'Creá una teoría: "Manuel concentra obra pública para favorecer a empresas vinculadas"',
];

export default function AutopilotPanel({ slug, summary, entities = [], onMutate, onNavigateTab }) {
  const [autoStatus, setAutoStatus] = useState(null);
  const [launching, setLaunching] = useState(false);
  const [launchErr, setLaunchErr] = useState(null);

  // Chat
  const [chatHistory, setChatHistory] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [chatErr, setChatErr] = useState(null);
  const [activeCite, setActiveCite] = useState(null);
  const [activeRag, setActiveRag] = useState([]);
  const [entityPopup, setEntityPopup] = useState(null);
  const bodyRef = useRef(null);

  const loadStatus = async () => {
    try {
      const s = await getAutoRunStatus(slug);
      setAutoStatus(s);
    } catch (e) {
      console.warn('autorun status err', e.message);
    }
  };

  const loadChat = async () => {
    try {
      const m = await getDossierChatHistory(slug);
      setChatHistory(m);
    } catch (e) {
      console.warn('chat history err', e.message);
    }
  };

  useEffect(() => { loadStatus(); loadChat(); /* eslint-disable-next-line */ }, [slug]);

  // Polling SOLO cuando hay un run actualmente activo. El flag loop_running
  // indica "queremos seguir iterando", pero entre runs no hay nada que polear.
  // Si polea por loop_running también, el setInterval queda prendido
  // permanentemente y dispara onMutate=load del parent, lo que re-renderiza
  // toda la página de la investigación cada pocos segundos.
  const prevActiveIdRef = useRef(null);
  useEffect(() => {
    const currentActiveId = autoStatus?.active?.id || null;
    // Detectar transición: estaba activo y ya no → notificar al parent UNA vez
    if (prevActiveIdRef.current && !currentActiveId && onMutate) {
      onMutate();
    }
    prevActiveIdRef.current = currentActiveId;

    if (!currentActiveId) return undefined;
    const t = setInterval(loadStatus, POLL_INTERVAL);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStatus?.active?.id]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [chatHistory, chatSending]);

  const launch = async () => {
    setLaunching(true); setLaunchErr(null);
    try {
      await launchAutoRun(slug);
      await loadStatus();
      if (onMutate) onMutate();
    } catch (e) {
      setLaunchErr(friendlyError(e));
    } finally { setLaunching(false); }
  };

  const startLoop = async () => {
    setLaunchErr(null);
    try {
      await setLoopState(slug, 'start');
      await loadStatus();
    } catch (e) {
      setLaunchErr(friendlyError(e));
    }
  };

  const pauseLoop = async () => {
    try { await setLoopState(slug, 'pause'); await loadStatus(); } catch (e) {
      setLaunchErr(friendlyError(e));
    }
  };

  const resetLoop = async () => {
    if (!window.confirm('¿Resetear contador del loop? (no borra el progreso, solo el ciclo).')) return;
    try { await setLoopState(slug, 'reset'); await loadStatus(); } catch (e) {
      setLaunchErr(friendlyError(e));
    }
  };

  // Auto-dispatch: cuando loop está running, no hay run activo y no excedimos el max,
  // lanzamos el siguiente autopilot.
  useEffect(() => {
    if (!autoStatus) return;
    const loop = autoStatus.loop || {};
    if (loop.status !== 'running') return;
    if (autoStatus.active) return; // ya hay uno corriendo
    if (launching) return;
    if ((loop.cycle || 0) >= (loop.max_cycles || 12)) {
      // alcanzamos el máximo → pausar
      setLoopState(slug, 'pause').then(loadStatus).catch(() => {});
      return;
    }
    // Lanzar siguiente ciclo
    const timeout = setTimeout(async () => {
      try {
        setLaunching(true);
        await launchAutoRun(slug);
        await setLoopState(slug, 'increment_cycle');
        await loadStatus();
        if (onMutate) onMutate();
      } catch (e) {
        setLaunchErr(friendlyError(e));
        // si falla, pausamos para no quedar en bucle de errores
        await setLoopState(slug, 'pause').catch(() => {});
      } finally {
        setLaunching(false);
      }
    }, 4000); // 4s gap entre ciclos
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStatus?.active, autoStatus?.loop?.status, autoStatus?.loop?.cycle]);

  const send = async (override) => {
    const q = (override ?? chatInput).trim();
    if (!q || chatSending) return;
    setChatErr(null);
    setChatHistory((h) => [...h, { role: 'user', content: q, created_at: new Date().toISOString() }]);
    setChatInput('');
    setChatSending(true);
    try {
      await sendDossierChat(slug, q);
      await Promise.all([loadChat(), loadStatus()]);
      if (onMutate) onMutate();
    } catch (e) {
      setChatErr(friendlyError(e));
    } finally { setChatSending(false); }
  };

  const clearChat = async () => {
    if (!window.confirm('¿Borrar todo el historial del chat?')) return;
    await clearDossierChatHistory(slug);
    setChatHistory([]);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const activeRun = autoStatus?.active;
  const activeSearches = autoStatus?.active_searches || [];
  const plan = autoStatus?.plan || [];
  const recentRuns = (autoStatus?.runs || []).slice(0, 3);
  const loop = autoStatus?.loop || { status: 'idle', cycle: 0, max_cycles: 12 };
  const isLooping = loop.status === 'running';
  const isPaused = loop.status === 'paused';

  return (
    <Split>
      <ColLeft>
        <StatsGrid>
          <div className="cell"><div className="label">Entidades</div><div className="value">{summary?.entities ?? 0}</div></div>
          <div className="cell"><div className="label">Conexiones</div><div className="value">{summary?.connections ?? 0}</div></div>
          <div className="cell"><div className="label">Informes</div><div className="value">{summary?.reports ?? 0}</div></div>
          <div className="cell"><div className="label">Teorías</div><div className="value">{summary?.theories ?? 0}</div></div>
          <div className="cell"><div className="label">Notas</div><div className="value">{summary?.notes ?? 0}</div></div>
          <div className="cell"><div className="label">Búsquedas</div><div className="value">{summary?.searches ?? 0}</div></div>
        </StatsGrid>

        <Card>
          <div className="head">
            <FaCog style={{ color: accent }} />
            <span className="title">Autopilot</span>
            {isLooping && <span className="pill pill-running"><FaInfinity style={{ marginRight: 4 }} /> Loop activo · ciclo {loop.cycle}/{loop.max_cycles}</span>}
            {isPaused && <span className="pill pill-running" style={{ color: 'var(--alert-warning, #f4a261)', borderColor: 'rgba(244,162,97,0.4)', background: 'rgba(244,162,97,0.08)' }}>Loop pausado · ciclo {loop.cycle}</span>}
            {!isLooping && !isPaused && activeRun && <span className="pill pill-running">Run #{activeRun.id} · paso {activeRun.current_step}/{activeRun.total_steps}</span>}
            {!isLooping && !isPaused && !activeRun && <span className="sub">listo para correr</span>}
          </div>

          <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
            {isLooping
              ? <>Trabajando en loop: cuando termine un ciclo (los 5 agentes en secuencia) arranca el siguiente automáticamente, hasta llegar al máximo de <strong>{loop.max_cycles}</strong> ciclos o cuando lo pauses.</>
              : 'El autopilot ejecuta los 5 agentes IA en secuencia. Con "Play loop" trabaja en ciclos continuos.'
            }
          </div>

          {!isLooping && !activeRun && (
            <div>
              <div style={{ fontFamily: mono, fontSize: '0.6rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--text-disabled)', marginBottom: 5 }}>
                Plan próximo ciclo ({plan.length} pasos)
              </div>
              <PlanList>
                {plan.map((p, i) => <li key={i}>{p}</li>)}
              </PlanList>
            </div>
          )}

          {activeRun && (
            <div style={{ color: 'var(--text-disabled)', fontSize: '0.83rem' }}>
              {activeRun.current_step_name && <><span className="pulse" />{activeRun.current_step_name}</>}
            </div>
          )}

          {launchErr && <ErrorBox>{launchErr}</ErrorBox>}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {!isLooping ? (
              <PrimaryBtn onClick={startLoop} disabled={launching}>
                <FaPlay /> {isPaused ? 'Reanudar loop' : 'Play loop'}
              </PrimaryBtn>
            ) : (
              <PrimaryBtn
                onClick={pauseLoop}
                style={{
                  background: 'rgba(244, 162, 97, 0.18)',
                  color: 'var(--alert-warning, #f4a261)',
                  border: '1px solid rgba(244, 162, 97, 0.4)',
                }}
              >
                <FaPause /> Pausar loop
              </PrimaryBtn>
            )}

            <button
              type="button"
              onClick={launch}
              disabled={launching || !!activeRun}
              style={{
                background: 'transparent',
                color: 'var(--text-secondary)',
                border: '1px solid ' + borderSoft,
                padding: '9px 14px',
                borderRadius: 8,
                fontSize: '0.82rem',
                cursor: launching || activeRun ? 'not-allowed' : 'pointer',
                opacity: launching || activeRun ? 0.5 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <FaPlay style={{ fontSize: '0.7rem' }} /> 1 ciclo
            </button>

            {(isLooping || isPaused || loop.cycle > 0) && (
              <button
                type="button"
                onClick={resetLoop}
                style={{
                  background: 'transparent',
                  color: 'var(--text-disabled)',
                  border: '1px solid ' + borderSoft,
                  padding: '9px 14px',
                  borderRadius: 8,
                  fontSize: '0.82rem',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <FaStop style={{ fontSize: '0.7rem' }} /> Reset
              </button>
            )}

            <span style={{ marginLeft: 'auto', fontSize: '0.74rem', color: 'var(--text-disabled)', alignSelf: 'center' }}>
              Cada ciclo tarda 3–10 min · max {loop.max_cycles} ciclos
            </span>
          </div>
        </Card>

        {(activeRun || activeSearches.length > 0) && (
          <Card>
            <div className="head">
              <FaListUl style={{ color: accent }} />
              <span className="title">Log en vivo del run #{activeRun?.id}</span>
            </div>
            {activeSearches.length === 0 && <div style={{ color: 'var(--text-disabled)' }}>Esperando primer paso…</div>}
            {activeSearches.map((s) => (
              <LogRow key={s.id} $status={s.status}>
                <span className="marker">
                  {s.status === 'running' ? <span className="pulse" /> :
                    s.status === 'done' ? <FaCheckCircle /> :
                    s.status === 'failed' ? <FaExclamationCircle /> : '·'}
                </span>
                <div className="body">
                  <span className="agent">paso {s.step_number} · {s.agent}</span>
                  {s.input && (
                    <div className="input">{JSON.stringify(s.input).slice(0, 220)}</div>
                  )}
                  <div className="meta">
                    {s.status}
                    {s.elapsed_ms ? ` · ${(s.elapsed_ms / 1000).toFixed(1)}s` : ''}
                    {s.error_message ? ' · ' + s.error_message.slice(0, 80) : ''}
                  </div>
                </div>
              </LogRow>
            ))}
          </Card>
        )}

        {recentRuns.length > 0 && (
          <Card>
            <div className="head">
              <FaHistory style={{ color: accent }} />
              <span className="title">Runs anteriores</span>
            </div>
            {recentRuns.map((r) => (
              <LogRow key={r.id} $status={r.status}>
                <span className="marker">
                  {r.status === 'running' ? <span className="pulse" /> :
                    r.status === 'done' ? <FaCheckCircle /> :
                    r.status === 'failed' ? <FaExclamationCircle /> : '·'}
                </span>
                <div className="body">
                  <span className="agent">Run #{r.id}</span>
                  <div className="input">
                    {r.total_steps} pasos · {r.triggered_by || 'manual'}
                  </div>
                  <div className="meta">
                    {new Date(r.started_at).toLocaleString('es-AR')}
                    {r.elapsed_ms ? ` · ${(r.elapsed_ms / 1000).toFixed(1)}s` : ''}
                    {r.error_message ? ' · ' + r.error_message.slice(0, 60) : ''}
                  </div>
                </div>
              </LogRow>
            ))}
          </Card>
        )}
      </ColLeft>

      <ChatWrap>
        <ChatHeader>
          <span className="icon"><FaRobot /></span>
          <span className="title">Chat con tools</span>
          {chatHistory.length > 0 && (
            <button type="button" className="clear" onClick={clearChat} title="Borrar historial">
              <FaTrashAlt />
            </button>
          )}
        </ChatHeader>
        <ChatBody ref={bodyRef}>
          {chatHistory.length === 0 && !chatSending && (
            <div style={{ color: 'var(--text-disabled)', fontSize: '0.83rem', lineHeight: 1.5 }}>
              Pedile al asistente cualquier cosa: agregar entidades, correr agentes,
              lanzar autopilot, crear teorías. Tiene acceso al estado del dossier +
              al índice RAG del HCD.
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 12 }}>
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => send(s)}
                    style={{
                      background: 'transparent',
                      border: '1px solid ' + borderSoft,
                      color: 'var(--text-secondary)',
                      padding: '6px 10px',
                      borderRadius: 6,
                      fontSize: '0.74rem',
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {chatHistory.map((m) => {
            const handleCite = (cite) => { setActiveRag(m.citations || []); setActiveCite(cite); };
            const handleEntity = (id) => {
              const e = entities.find((x) => Number(x.id) === Number(id));
              setEntityPopup(e || { id, missing: true });
            };
            const handleAgent = (agent) => {
              send(`Corré el agente ${agent} con el contexto que ya tenés sobre esta investigación.`);
            };
            const components = m.role === 'assistant'
              ? wrapChatMarkdown({ onCite: handleCite, onEntity: handleEntity, onAgent: handleAgent })
              : null;
            return (
              <Msg key={m.id || m.created_at} $role={m.role}>
                <div className="avatar">{m.role === 'user' ? <FaUser /> : <FaRobot />}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="bubble">
                    {m.role === 'assistant' ? (
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
                        {displayAssistantContent(m.content)}
                      </ReactMarkdown>
                    ) : m.content}
                  </div>
                  {Array.isArray(m.actions) && m.actions.length > 0 && (
                    <div className="actions-pill">
                      ✓ {m.actions.filter((a) => a.ok).map((a) => a.tool).join(', ')}
                    </div>
                  )}
                </div>
              </Msg>
            );
          })}
          {chatSending && (
            <Msg $role="assistant">
              <div className="avatar"><FaRobot /></div>
              <div className="bubble"><Typing><span /><span /><span /></Typing></div>
            </Msg>
          )}
          {chatErr && <ErrorBox style={{ margin: 0 }}><FaExclamationCircle /> {chatErr}</ErrorBox>}
        </ChatBody>
        <InputWrap onSubmit={(e) => { e.preventDefault(); send(); }}>
          <ChatInput
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Pedile algo… (Enter envía · Shift+Enter salto)"
            disabled={chatSending}
            rows={1}
          />
          <SendBtn type="submit" disabled={chatSending || !chatInput.trim()} aria-label="Enviar">
            <FaPaperPlane />
          </SendBtn>
        </InputWrap>
      </ChatWrap>

      {activeCite && (
        <CitePopup
          cite={activeCite}
          indexed={[]}
          fresh={[]}
          rag={activeRag}
          onClose={() => setActiveCite(null)}
        />
      )}

      {entityPopup && (
        <EntityPopupBackdrop onClick={() => setEntityPopup(null)}>
          <EntityPopupCard onClick={(e) => e.stopPropagation()}>
            <button type="button" className="close" onClick={() => setEntityPopup(null)} aria-label="Cerrar">×</button>
            {entityPopup.missing ? (
              <>
                <h3>Entidad #{entityPopup.id}</h3>
                <div className="missing">
                  No encontré la entidad con id <strong>{entityPopup.id}</strong> en este dossier. Probable error del LLM o entidad eliminada.
                </div>
              </>
            ) : (
              <>
                <h3>{entityPopup.name}</h3>
                <div className="meta">
                  id:{entityPopup.id} · {entityPopup.type} · importancia {entityPopup.importance} · {entityPopup.status}
                </div>
                {entityPopup.summary && <div className="summary">{entityPopup.summary}</div>}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {onNavigateTab && (
                    <button
                      type="button"
                      onClick={() => { onNavigateTab('entities'); setEntityPopup(null); }}
                      style={{
                        background: 'rgba(244,162,97,0.1)',
                        border: '1px solid rgba(244,162,97,0.4)',
                        color: 'var(--alert-warning, #f4a261)',
                        padding: '7px 11px', borderRadius: 6,
                        fontSize: '0.82rem', cursor: 'pointer',
                      }}
                    >
                      Ver tab Entidades
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      send(`Profundizá la investigación sobre ${entityPopup.name} con entity_researcher.`);
                      setEntityPopup(null);
                    }}
                    style={{
                      background: 'rgba(98,213,154,0.1)',
                      border: '1px solid rgba(98,213,154,0.4)',
                      color: '#62d59a',
                      padding: '7px 11px', borderRadius: 6,
                      fontSize: '0.82rem', cursor: 'pointer',
                    }}
                  >
                    ▶ Investigar a fondo
                  </button>
                </div>
              </>
            )}
          </EntityPopupCard>
        </EntityPopupBackdrop>
      )}
    </Split>
  );
}
